// src/pages/BuyCars.jsx - UPDATED: all car links use SEO-friendly slugs
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/BuyCars.css";
import { searchCarsAdvanced } from "../services/car";
import ShortlistButton from "../components/ShortlistButton";
import SearchFilters from "../components/SearchFilters";
import { useCity } from "../context/CityContext";
import { buildCarSlug } from "../utils/carSlug"; // ✅ NEW

// ✅ FIX: Central image URL builder — works on localhost AND production server
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const toAbs = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
};

const SkeletonCard = () => (
  <div className="bc-card-wrapper">
    <div className="bc-car-card skeleton-card">
      <div className="skeleton-img" />
      <div className="skeleton-info">
        <div className="skeleton-line long" />
        <div className="skeleton-line medium" />
        <div className="skeleton-line short" />
      </div>
    </div>
  </div>
);

const StarRating = ({ rating }) => (
  <div className="bc-star-rating">
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        className={`bc-star ${star <= Math.round(rating) ? "filled" : "empty"}`}
      >
        ★
      </span>
    ))}
  </div>
);

const ReviewSkeleton = () => (
  <div className="bc-review-skeleton">
    <div className="bc-review-skel-header">
      <div className="bc-skel-avatar" />
      <div style={{ flex: 1 }}>
        <div className="bc-skel-line w60" />
        <div className="bc-skel-line w40" />
      </div>
    </div>
    <div className="bc-skel-line w90" />
    <div className="bc-skel-line w75" />
  </div>
);

// ── EMI helper: simple flat-rate estimate ──
const calcEMI = (price) => {
  const p = parseFloat(price);
  if (!p || p <= 0) return null;
  const r = 8.5 / 12 / 100;
  const n = 60;
  const emi = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Math.round(emi);
};

const BuyCars = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isComingSoon = params.get("comingSoon") === "true";
  const urlCity = params.get("location");
  const navigate = useNavigate();
  const { selectedCity } = useCity();

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCars, setTotalCars] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [suggestedCars, setSuggestedCars] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [avgRating, setAvgRating] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const debounceTimer = useRef(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const PER_PAGE = 20;

  const queryParams = new URLSearchParams(location.search);
  const displayCity = queryParams.get("location") || selectedCity || "India";

  const getFilterParams = useCallback(
    (page = 1) => {
      const p = { page, per_page: PER_PAGE };
      const loc = queryParams.get("location") || selectedCity;
      if (loc && loc !== "All Cities") p.location = loc;
      [
        "q", "brand", "model", "fuelType", "transmission", "bodyType",
        "budget", "year", "kmDriven", "minPrice", "maxPrice", "minYear",
        "maxYear", "minKm", "maxKm", "color", "seats", "ownerType",
        "availability", "rto",
      ].forEach((k) => {
        if (queryParams.get(k)) p[k] = queryParams.get(k);
      });
      return p;
    },
    [location.search, selectedCity]
  );

  const fetchSuggested = useCallback(async () => {
    try {
      const res = await searchCarsAdvanced({ page: 1, per_page: 5 });
      if (res?.data) setSuggestedCars(res.data);
    } catch {
      setSuggestedCars([]);
    }
  }, []);

  useEffect(() => {
    fetchSuggested();
  }, [fetchSuggested]);

  useEffect(() => {
    const fetchReviews = async () => {
      setReviewsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${API_BASE}/api/v1/reviews`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : data.reviews || data.data || [];
        setReviews(list.slice(0, 3));
        if (list.length > 0) {
          const avg =
            list.reduce((s, r) => s + (r.rating || 0), 0) / list.length;
          setAvgRating(avg.toFixed(1));
        }
      } catch {
        setReviews([]);
        setAvgRating(0);
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setCars([]);
      setCurrentPage(1);
      setHasMore(true);
      try {
        const res = await searchCarsAdvanced(getFilterParams(1));
        if (res === null) return;
        const newCars = res.data || [];
        setCars(newCars);
        setTotalCars(res.total || 0);
        setHasMore(
          newCars.length === PER_PAGE && (res.total || 0) > PER_PAGE
        );
      } catch (e) {
        console.error(e);
        setCars([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [location.search, getFilterParams]);

  useEffect(() => {
    const loc = queryParams.get("location");
    if (!loc && selectedCity && selectedCity !== "All Cities") {
      const p = new URLSearchParams(location.search);
      p.set("location", selectedCity);
      navigate(`/buy-cars?${p.toString()}`, { replace: true });
    }
  }, [selectedCity]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = currentPage + 1;
      const res = await searchCarsAdvanced(getFilterParams(next));
      if (res === null) return;
      const newCars = res.data || [];
      setCars((p) => [...p, ...newCars]);
      setCurrentPage(next);
      setHasMore(newCars.length === PER_PAGE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentPage, getFilterParams]);

  useEffect(() => {
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && hasMore && !loading && !loadingMore)
          loadMore();
      },
      { threshold: 0.1, rootMargin: "300px" }
    );
    if (loadMoreRef.current) observerRef.current.observe(loadMoreRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  const handleFilterChange = (newFilters) => {
    const p = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== "") p.append(k, v);
    });
    navigate(`/buy-cars?${p.toString()}`);
    setSidebarOpen(false);
  };

  const clearAllFilters = () => navigate("/buy-cars");

  const activeCount = Array.from(queryParams.entries()).filter(
    ([k]) => k !== "location" && k !== "page" && k !== "comingSoon"
  ).length;

  const getResultTitle = () => {
    const brand = queryParams.get("brand");
    const model = queryParams.get("model");
    const parts = ["Used"];
    if (brand) parts.push(brand);
    if (model) parts.push(model);
    parts.push("Cars");
    if (displayCity && displayCity !== "All Cities")
      parts.push(`in ${displayCity}`);
    return parts.join(" ");
  };

  const fmt = (price) =>
    `₹${parseFloat(price).toLocaleString("en-IN")}`;

  const fmtEMI = (price) => {
    const emi = calcEMI(price);
    if (!emi) return null;
    return `EMI ₹${emi.toLocaleString("en-IN")}/m*`;
  };

  const getReviewerName = (r) =>
    r.user?.name ||
    r.user?.email?.split("@")[0] ||
    r.reviewer_name ||
    "Customer";

  const getReviewDate = (r) => {
    const raw = r.created_at || r.date || r.review_date;
    if (!raw) return "";
    try {
      return new Date(raw).toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="bc-wrapper">
      <div className="bc-page-layout">

        {sidebarOpen && (
          <div
            className="bc-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <aside className={`bc-sidebar ${sidebarOpen ? "bc-sidebar--open" : ""}`}>
          <button
            className="bc-sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
          >
            ✕ Close
          </button>
          <SearchFilters onFilterChange={handleFilterChange} />
        </aside>

        <main className="bc-main">
          <div className="bc-container">

            <div className="bc-results-header">
              <div className="bc-results-left">
                <button
                  className={`bc-filter-toggle-btn ${
                    sidebarOpen ? "bc-filter-toggle-btn--active" : ""
                  }`}
                  onClick={() => setSidebarOpen((prev) => !prev)}
                  aria-label="Toggle filters"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="20" y2="12" />
                    <line x1="12" y1="18" x2="20" y2="18" />
                  </svg>
                  <span>{sidebarOpen ? "Hide Filters" : "Filters"}</span>
                  {activeCount > 0 && (
                    <span className="bc-filter-badge">{activeCount}</span>
                  )}
                </button>

                <h1 className="bc-results-title">{getResultTitle()}</h1>
                <span className="bc-results-count">
                  {loading
                    ? "..."
                    : `${totalCars.toLocaleString("en-IN")} cars`}
                </span>
              </div>
              {activeCount > 0 && (
                <button className="bc-clear-btn" onClick={clearAllFilters}>
                  ✕ Clear ({activeCount})
                </button>
              )}
            </div>

            <section className="bc-grid-section">
              {isComingSoon && (
                <div className="coming-soon-box">
                  🚧 Cars are not available in {urlCity} yet. <br />
                  We are launching soon in your city!
                </div>
              )}

              {loading ? (
                <div className="bc-grid">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : isComingSoon ? null : cars.length === 0 ? (
                <div className="bc-no-cars">
                  <div className="bc-no-cars-icon">🚗</div>
                  <h3>No cars found</h3>
                  <p>
                    Our services will be available in this city shortly.
                    <br />
                    Try adjusting your filters.
                  </p>
                  <button className="bc-reset-btn" onClick={clearAllFilters}>
                    View All Cars
                  </button>
                </div>
              ) : (
                <>
                  <div className="bc-grid">
                    {cars.map((car) => (
                      <div key={car.id} className="bc-card-wrapper">
                        {/* ✅ NEW: use buildCarSlug instead of /car/${car.id} */}
                        <Link to={buildCarSlug(car)} className="bc-car-card">

                          <div className="bc-car-img">
                            <div className="bc-heart">
                              <ShortlistButton carId={car.id} size="medium" />
                            </div>
                            {car.images_urls?.[0] ? (
                              <img
                                src={toAbs(car.images_urls[0])}
                                alt={`${car.brand} ${car.model}`}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/placeholder-car.png";
                                }}
                              />
                            ) : (
                              <div className="bc-img-placeholder">🚗</div>
                            )}
                          </div>

                          <div className="bc-car-info">
                            <h3 className="bc-car-title">
                              {car.brand} {car.model}
                            </h3>
                            {car.variant && (
                              <p className="bc-car-variant">{car.variant}</p>
                            )}

                            <div className="bc-car-specs">
                              {car.km_driven && (
                                <span className="bc-spec-tag">
                                  {Number(car.km_driven).toLocaleString("en-IN")} km
                                </span>
                              )}
                              {car.fuel_type && (
                                <span className="bc-spec-tag">{car.fuel_type}</span>
                              )}
                              {car.transmission && (
                                <span className="bc-spec-tag">{car.transmission}</span>
                              )}
                              {car.rto && (
                                <span className="bc-spec-tag bc-spec-rto">
                                  {car.rto.toUpperCase()}
                                </span>
                              )}
                            </div>

                            <p className="bc-car-location">
                              📍 {car.location} &nbsp;·&nbsp; {car.year}
                            </p>

                            <div className="bc-price-row">
                              <span className="bc-car-price">{fmt(car.price)}</span>
                              {fmtEMI(car.price) && (
                                <span className="bc-car-emi">{fmtEMI(car.price)}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>

                  <div ref={loadMoreRef} className="bc-load-more">
                    {loadingMore && (
                      <div className="bc-spinner-wrap">
                        <div className="bc-spinner" />
                        <span>Loading more cars...</span>
                      </div>
                    )}
                    {!hasMore && cars.length > 0 && (
                      <div className="bc-end-msg">
                        ✓ All {totalCars.toLocaleString("en-IN")} cars loaded
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        </main>
      </div>

      {/* ── Below fold ── */}
      <div className="bc-below-fold">
        {!loading && suggestedCars.length > 0 && (
          <section className="bc-section bc-might-like">
            <h2 className="bc-section-heading">You Might Like</h2>
            <div className="bc-suggest-scroll">
              {suggestedCars.map((car) => (
                // ✅ NEW: use buildCarSlug
                <Link
                  key={car.id}
                  to={buildCarSlug(car)}
                  className="bc-suggest-card"
                >
                  <div className="bc-suggest-img">
                    {car.images_urls?.[0] ? (
                      <img
                        src={toAbs(car.images_urls[0])}
                        alt={`${car.brand} ${car.model}`}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder-car.png";
                        }}
                      />
                    ) : (
                      <div className="bc-img-placeholder">🚗</div>
                    )}
                  </div>
                  <div className="bc-suggest-info">
                    <h4 className="bc-suggest-title">
                      {car.brand} {car.model}
                    </h4>
                    <div className="bc-suggest-specs">
                      {car.km_driven && (
                        <span>
                          {Number(car.km_driven).toLocaleString("en-IN")} km
                        </span>
                      )}
                      {car.fuel_type && <span>{car.fuel_type}</span>}
                      {car.transmission && <span>{car.transmission}</span>}
                      {car.rto && <span>{car.rto.toUpperCase()}</span>}
                    </div>
                    <div className="bc-price-row">
                      <p className="bc-suggest-price">{fmt(car.price)}</p>
                      {fmtEMI(car.price) && (
                        <span className="bc-car-emi">{fmtEMI(car.price)}</span>
                      )}
                    </div>
                    <p className="bc-suggest-location">📍 {car.location}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!loading && (
          <section className="bc-section bc-reviews">
            <div className="bc-reviews-top">
              <div className="bc-reviews-score-box">
                <span className="bc-score-num">{avgRating || "—"}</span>
                <StarRating rating={Math.round(Number(avgRating))} />
              </div>
              <div>
                <h2 className="bc-reviews-heading">
                  User Reviews &amp; Ratings
                </h2>
                <p className="bc-reviews-sub">Based on verified purchases</p>
              </div>
            </div>
            {reviewsLoading && (
              <div className="bc-reviews-loading">
                {[1, 2, 3].map((i) => (
                  <ReviewSkeleton key={i} />
                ))}
              </div>
            )}
            {!reviewsLoading && reviews.length > 0 && (
              <div className="bc-reviews-grid">
                {reviews.map((r) => (
                  <div key={r.id} className="bc-review-card">
                    <div className="bc-review-header">
                      <div className="bc-avatar">
                        {getReviewerName(r).charAt(0).toUpperCase()}
                      </div>
                      <div className="bc-reviewer-meta">
                        <p className="bc-reviewer-name">{getReviewerName(r)}</p>
                        <p className="bc-reviewer-date">
                          {getReviewDate(r)}
                          {(r.user?.city || r.car?.location || displayCity) && (
                            <>
                              {" "}
                              ·{" "}
                              {r.user?.city ||
                                r.car?.location ||
                                (displayCity !== "All Cities"
                                  ? displayCity
                                  : "India")}
                            </>
                          )}
                        </p>
                      </div>
                      <StarRating rating={r.rating || 5} />
                    </div>
                    <p className="bc-review-text">
                      {r.comment || r.review || r.text || "Great experience!"}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {!reviewsLoading && reviews.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 0",
                  color: "#888",
                  fontSize: "14px",
                }}
              >
                No reviews yet. Be the first to share your experience!
              </div>
            )}
            <div className="bc-view-all-wrap">
              <Link to="/reviews" className="bc-view-all-btn">
                View all Reviews
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BuyCars;